export class WePlpPageObserver {
  constructor () {
    this.currentUrl = this.urlToPathName(window.location.href)
    this.isCollectionPage = this.currentUrl.includes('/collections/')
    this.currentPage = null
    this.observer = null
    this.initialPageFound = false

    this.init()
  }

  init () {
    if (!this.isCollectionPage) {
      return
    }

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupObserver()
      })
    } else {
      this.setupObserver()
    }
  }

  setupObserver () {
    // Find all product cards with data-plp-page attribute
    const productCards = document.querySelectorAll('[data-plp-page]')

    if (productCards.length === 0) {
      // Try again after a short delay in case products are still loading
      setTimeout(() => {
        this.setupObserver()
      }, 500)
      return
    }

    // Check for initial page
    this.checkInitialPage(productCards)

    // Create Intersection Observer
    const options = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.1, // Trigger when 10% of the element is visible
    }

    this.observer = new IntersectionObserver((entries) => {
      // Get all currently intersecting cards
      const intersectingCards = entries
        .filter(entry => entry.isIntersecting)
        .map(entry => ({
          element: entry.target,
          page: entry.target.getAttribute('data-plp-page'),
          top: entry.boundingClientRect.top,
        }))
        .filter(card => card.page !== null)

      if (intersectingCards.length > 0) {
        // Find the page of the card closest to the top of the viewport
        const topCard = intersectingCards.reduce((closest, card) => {
          return card.top < closest.top ? card : closest
        })

        if (topCard.page) {
          this.handlePageDetection(topCard.page)
        }
      }
    }, options)

    // Observe all product cards
    productCards.forEach((card) => {
      this.observer.observe(card)
    })

    // Also observe dynamically added cards (for AJAX-loaded content)
    this.observeNewCards()
  }

  checkInitialPage (productCards) {
    // Find the first visible product card to determine initial page
    const visibleCards = Array.from(productCards).filter((card) => {
      const rect = card.getBoundingClientRect()
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      )
    })

    // If no cards are fully visible, check which one is closest to the top
    let initialCard = null
    if (visibleCards.length > 0) {
      initialCard = visibleCards[0]
    } else {
      // Find the card closest to the top of the viewport
      initialCard = Array.from(productCards).reduce((closest, card) => {
        const cardRect = card.getBoundingClientRect()
        const closestRect = closest.getBoundingClientRect()
        return cardRect.top < closestRect.top ? card : closest
      })
    }

    if (initialCard) {
      const initialPage = initialCard.getAttribute('data-plp-page')
      if (initialPage) {
        this.currentPage = initialPage
        this.initialPageFound = true
      }
    }
  }

  handlePageDetection (page) {
    if (this.currentPage === null) {
      // First page detection
      this.currentPage = page
    } else if (this.currentPage !== page) {
      // Page changed - update current URL with new page value
      const updatedUrl = this.updateUrlWithPage(page)
      
      // Update the browser URL without reloading the page
      window.history.replaceState({}, '', updatedUrl)
      
      this.currentPage = page
    }
  }

  updateUrlWithPage (pageValue) {
    try {
      // Get current URL
      const currentUrl = new URL(window.location.href)
      
      // Update or add the page query parameter
      currentUrl.searchParams.set('page', String(pageValue))
      
      // Return the updated URL (pathname + search, without origin)
      return currentUrl.pathname + currentUrl.search
    } catch (e) {
      // Fallback: manual URL manipulation if URL API fails
      const currentHref = window.location.href
      const urlWithoutQuery = currentHref.split('?')[0]
      const queryString = currentHref.split('?')[1] || ''
      
      // Parse existing query parameters
      const params = new URLSearchParams(queryString)
      params.set('page', String(pageValue))
      
      const updatedUrl = `${urlWithoutQuery}?${params.toString()}`
      // Extract pathname and search from the full URL
      const urlObj = new URL(updatedUrl)
      return urlObj.pathname + urlObj.search
    }
  }

  observeNewCards () {
    // Use MutationObserver to watch for new product cards added to the DOM
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node is a product card
            if (node.hasAttribute && node.hasAttribute('data-plp-page')) {
              this.observer.observe(node)
            }

            // Check if the added node contains product cards
            const cards = node.querySelectorAll && node.querySelectorAll('[data-plp-page]')
            if (cards) {
              cards.forEach((card) => {
                this.observer.observe(card)
              })
            }
          }
        })
      })
    })

    // Observe changes to the document body
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })
  }

  urlToPathName (url) {
    try {
      const object = new URL(url)
      return object.pathname
    } catch (e) {
      return url.split('?')[0]
    }
  }

  disconnect () {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}

// Initialize immediately and also on DOMContentLoaded
// This handles both full page loads and AJAX-loaded content
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // eslint-disable-next-line no-new
    new WePlpPageObserver()
  })
} else {
  // DOM already loaded
  // eslint-disable-next-line no-new
  new WePlpPageObserver()
}
